import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spin } from 'antd';
import PropTypes from 'prop-types';
import moment from 'moment';
import TableHeader from '../../components/TableHeader';
import Table from '../../components/Table';
import ModalCreateEstablishment from '../ModalEstablishment/ModalCreateEstablishment';
import ButtonSetting from '../../components/ButtonSetting';
import ModalUpdateEstablishment from '../ModalEstablishment/ModalUpdateEstablishment';
import { MAX_LICENSES_COUNT } from '../../utils/const';

const columns = ({ openUpdateEstablishmentModal, locales }) => [
  {
    title: locales.admin.name,
    dataIndex: 'name',
    key: 'name',
    render: (name) => {
      const displayName =
        name && name.length > 200 ? `${name.substring(0, 60)}...` : name || '';
      return (
        <p
          title={name || ''}
          className="overflow-hidden text-wrap"
          style={{ maxWidth: '200px' }}
        >
          {displayName}
        </p>
      );
    },
  },
  {
    title: locales.admin.educator,
    dataIndex: 'lecturersCount',
    key: 'lecturersCount',
  },
  {
    title: locales.admin.class,
    dataIndex: 'classesCount',
    key: 'classesCount',
  },
  {
    title: locales.admin.project,
    dataIndex: 'bmcCount',
    key: 'bmcCount',
  },
  {
    title: locales.admin.student,
    dataIndex: 'studentTotal',
    key: 'studentTotal',
  },
  {
    title: locales.admin.activeStudent,
    dataIndex: 'activeStudents',
    key: 'activeStudents',
  },
  {
    title: locales.admin.licenseExpire,
    dataIndex: 'expireAt',
    key: 'expireAt',
    render: (expireAt) => {
      const date = new Date(expireAt);
      moment.locale('en');
      return moment(date).format('DD MMM YYYY');
    },
  },
  {
    title: locales.admin.licenses,
    dataIndex: 'maxLicensesCount',
    key: 'maxLicensesCount',
    render: (maxLicensesCount) =>
      maxLicensesCount === MAX_LICENSES_COUNT ? '-' : maxLicensesCount,
  },
  {
    title: '',
    key: 'id',
    render: (record) => (
      <ButtonSetting
        onClick={(event) => {
          event.stopPropagation();
          openUpdateEstablishmentModal(record.id);
        }}
      />
    ),
  },
];

const EstablishmentLists = ({ locales, rowClick, rowClickable }) => {
  const [search, setSearch] = useState(undefined);
  const [visibleCreateEstablishmentModal, setVisibleCreateEstablishmentModal] =
    useState(false);
  const [visibleUpdateEstablishmentModal, setVisibleUpdateEstablishmentModal] =
    useState(false);
  const [updateEstablishmentId, setUpdateEstablishmentId] = useState(null);

  const {
    establishments: { data, meta, loading },
    establishment: { uploading },
    pagination: { pageSize: initPageSize },
  } = useSelector((state) => state);

  const {
    establishments: { fetchEstablishments, reload, sortEstablishments },
    establishment: { createEstablishment },
    admin: { clearSelectedCreatedAdmins },
  } = useDispatch();

  const handleCloseCreateEstablishmentModal = () => {
    setVisibleCreateEstablishmentModal(false);
    clearSelectedCreatedAdmins();
  };

  const handleOpenCreateEstablishmentModal = () => {
    setVisibleCreateEstablishmentModal(true);
  };

  const handleCloseUpdateEstablishmentModal = () => {
    setVisibleUpdateEstablishmentModal(false);
    setUpdateEstablishmentId(null);
  };

  const handleOpenUpdateEstablishmentModal = (establishmentId) => {
    setUpdateEstablishmentId(establishmentId);
    setVisibleUpdateEstablishmentModal(true);
  };

  const reloadList = () => reload();

  const handleCreateEstablishment = (values, afterSuccess) => {
    createEstablishment({
      values,
      after: [handleCloseCreateEstablishmentModal, reloadList, afterSuccess],
    });
  };

  useEffect(() => {
    fetchEstablishments({ search, limit: initPageSize });
  }, [search, initPageSize]);
  const handleSort = (values) => {
    sortEstablishments(values);
  };
  return (
    <>
      <ModalCreateEstablishment
        visible={visibleCreateEstablishmentModal}
        onClose={handleCloseCreateEstablishmentModal}
        onFinish={handleCreateEstablishment}
        uploading={uploading}
      />
      <ModalUpdateEstablishment
        visible={visibleUpdateEstablishmentModal}
        establishmentId={updateEstablishmentId}
        onClose={handleCloseUpdateEstablishmentModal}
        locales={locales}
      />
      <TableHeader
        title={locales.admin.establishments}
        count={loading ? <Spin /> : meta?.count || 0}
        textCreate={locales.admin.create_new_establishment}
        onCreate={handleOpenCreateEstablishmentModal}
        onSearch={(searchValue) => {
          setSearch(searchValue);
        }}
        searchLoading={loading}
      />
      <Table
        columns={columns({
          openUpdateEstablishmentModal: handleOpenUpdateEstablishmentModal,
          locales,
        })}
        dataSource={data}
        loading={loading}
        total={meta?.count || 0}
        current={meta?.page || 1}
        onChange={(pagination) => {
          const { current, pageSize } = pagination;
          fetchEstablishments({
            page: current,
            limit: pageSize,
          });
        }}
        onRow={(record) => ({
          onClick: () => rowClick(record),
        })}
        rowClickable={rowClickable}
        onSort={handleSort}
        meta={meta}
      />
    </>
  );
};

EstablishmentLists.propTypes = {
  rowClick: PropTypes.func,
  locales: PropTypes.object.isRequired,
  rowClickable: PropTypes.bool,
};
export default EstablishmentLists;
